import { type ChatModel, type ChatSession, chatSessionListDefault, estimateCostUSD, getModelConfig, getModelProvider } from "@acme/util";

export type UsageRange = 7 | 30 | 90 | 'all';

export interface ModelUsageRow {
  model: ChatModel;
  label: string;
  provider: string;
  replies: number;
  /** Provider-reported tokens where available, local estimate otherwise. */
  tokens: number;
  costUSD?: number;
}

export interface UsageStats {
  messagesSent: number;
  replies: number;
  activeChats: number;
  /** Tokens the provider reported (input + output). */
  reportedTokens: number;
  /** Tokens estimated locally for replies that predate usage capture. */
  estimatedTokens: number;
  costUSD: number;
  /** Replies whose cost could be priced (real usage + a verified list price). */
  pricedReplies: number;
  /** One entry per day, oldest first; only for bounded ranges. */
  daily: { date: string; sent: number }[];
  byModel: ModelUsageRow[];
}

/** Sample chats shipped with the app; they are not the user's activity. */
const SAMPLE_SESSION_IDS = new Set(chatSessionListDefault.map(s => s.id));

const dayKey = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Aggregate usage from the chats stored in this browser. Everything is derived
 * from local data: nothing is sent anywhere, and synced chats only count once
 * they are loaded locally.
 */
export const computeUsageStats = (sessions: ChatSession[], range: UsageRange, now = new Date()): UsageStats => {
  const since = range === 'all' ? null : new Date(now.getTime() - (range - 1) * 86_400_000);
  if (since) since.setUTCHours(0, 0, 0, 0);

  const daily = new Map<string, number>();
  if (since) {
    for (let i = 0; i < (range as number); i++) {
      daily.set(dayKey(new Date(since.getTime() + i * 86_400_000)), 0);
    }
  }

  const models = new Map<string, ModelUsageRow>();
  let messagesSent = 0, replies = 0, reportedTokens = 0, estimatedTokens = 0, costUSD = 0, pricedReplies = 0;
  const activeChats = new Set<string>();

  for (const session of sessions) {
    if (SAMPLE_SESSION_IDS.has(session.id)) continue;
    for (const message of session.messages || []) {
      const created = new Date(message.created);
      if (Number.isNaN(created.getTime())) continue;
      if (since && created < since) continue;
      activeChats.add(session.id);

      if (message.senderRole === 'user') {
        messagesSent++;
        const key = dayKey(created);
        if (daily.has(key)) daily.set(key, (daily.get(key) || 0) + 1);
        continue;
      }
      if (message.senderRole !== 'assistant' || message.error) continue;

      replies++;
      const model = (message.model || session.defaultModel || '') as ChatModel;
      const real = message.usage ? message.usage.inputTokens + message.usage.outputTokens : 0;
      const tokens = real || message.totalTokens || 0;
      if (real) reportedTokens += real; else estimatedTokens += tokens;
      const cost = estimateCostUSD(model, message.usage);
      if (cost !== undefined) { costUSD += cost; pricedReplies++; }

      const row = models.get(model) || {
        model,
        label: getModelConfig(model)?.displayName || model || 'Unknown model',
        provider: getModelProvider(model) || 'other',
        replies: 0,
        tokens: 0,
      };
      row.replies++;
      row.tokens += tokens;
      if (cost !== undefined) row.costUSD = (row.costUSD || 0) + cost;
      models.set(model, row);
    }
  }

  return {
    messagesSent,
    replies,
    activeChats: activeChats.size,
    reportedTokens,
    estimatedTokens,
    costUSD,
    pricedReplies,
    daily: Array.from(daily, ([date, sent]) => ({ date, sent })),
    byModel: Array.from(models.values()).sort((a, b) => b.replies - a.replies || b.tokens - a.tokens),
  };
};

export const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
};

export const formatUSD = (n: number): string =>
  n === 0 ? '$0' : n < 0.01 ? '<$0.01' : `$${n.toFixed(n < 10 ? 2 : 0)}`;
